import ActionButton from 'components/common/ActionButton';
import FalconComponentCard from 'components/common/FalconComponentCard';
import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  FormLabel,
  InputGroup,
  Offcanvas,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';

import {
  faLocationArrow,
  faPlusCircle,
  faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import corner3 from 'assets/img/illustrations/corner-3.png';
import Flex from 'components/common/Flex';
import Pagination from 'components/common/Pagination';
import locationHelper from 'helpers/location-helper';
import _ from 'lodash';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import SimpleBarReact from 'simplebar-react';
import { toast } from 'react-toastify';
import { showAlert, showConfirm } from 'stores/modal';
import api from 'util/api';
import logger from 'util/logger';
import FavoriteAddressForm from './FavoriteAddressForm';
import { useAsyncDebounce } from 'react-table';
import {
  useAddSavedLocationMutation,
  useEnumerateLocationsQuery,
  useRemoveSavedLocationMutation,
  useUpdateSavedLocationMutation
} from 'rtk/location.slice';

const FavoriteLocations = () => {
  const [favoriteLocations, setFavoriteLocations] = useState([]);

  const [sortBy, setSortBy] = useState('Created');
  const [isAsc, setIsAsc] = useState(false);

  const [pageSize, setPageSize] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [endOfResults, setEndOfResults] = useState(false);

  const [preAddress, setPreAddress] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [formTitle, setFormTitle] = useState(null);

  const { register } = useForm();

  const dispatch = useDispatch();

  const skip = pageIndex * pageSize;
  const {
    data: enumerateRes,
    isFetching,
    isLoading,
    isError: favoriteLocationLoadError,
    error: enumerateError,
    refetch
  } = useEnumerateLocationsQuery({
    skip,
    keyword: searchKeyword,
    sortKey: sortBy,
    sortOrderAsc: isAsc,
    pageSize
  });

  const loading = isFetching || isLoading;

  const [addSavedLocation, { isLoading: isAddingLocation }] =
    useAddSavedLocationMutation();
  const [updateSavedLocation, { isLoading: isUpdatingLocation }] =
    useUpdateSavedLocationMutation();
  const [removeSavedLocation, { isLoading: isRemovingLocation }] =
    useRemoveSavedLocationMutation();

  const savingLocation = isAddingLocation || isUpdatingLocation;

  const saveFavouriteLocation = location => {
    if (preAddress == null) {
      addSavedLocation({
        displayName: location.location_name,
        address1: location.address_1,
        address2: location.address_2,
        city: location.city,
        state: location.state,
        postalCode: location.zip
      })
        .unwrap()
        .then(() => {
          setShowAddressForm(false);
        })
        .catch(() => {
          dispatch(
            showAlert(
              'Error Deleting Favorite location',
              'There was a problem removing this favoritel Location.  Please try again.'
            )
          );
        });
    } else {
      updateSavedLocation({
        guid: preAddress.GUID,
        displayName: location.location_name,
        address1: location.address_1,
        address2: location.address_2,
        city: location.city,
        state: location.state,
        postalCode: location.zip
      })
        .unwrap()
        .then(() => {
          setShowAddressForm(false);
        })
        .catch(() => {
          dispatch(
            showAlert(
              'Error Deleting Favorite location',
              'There was a problem removing this favoritel Location.  Please try again.'
            )
          );
        });
    }
  };

  // const MarkFavoriteLocation = favoriteLocation => {
  //   let location = Object.assign({}, favoriteLocation);

  //   api.SavedLocations.update(
  //     location.GUID,
  //     location.DisplayName,
  //     location.Address1,
  //     '',
  //     location.City,
  //     location.State,
  //     location.PostalCode,
  //     !location.IsFavorite
  //   )
  //     .then(res => {
  //       setSavingLocation(false);
  //       loadFavoriteLocations();
  //       setShowAddressForm(false);
  //     })
  //     .catch(err => {
  //       setSavingLocation(false);
  //       dispatch(
  //         showAlert(
  //           'Error marking Favorite location',
  //           'There was a problem marking this favoritel Location.  Please try again.'
  //         )
  //       );
  //       loadFavoriteLocations();
  //     });
  // };

  const editFavoriteLocation = favoriteLocation => {
    setPreAddress(favoriteLocation);
    setFormTitle('Update location');
    setShowAddressForm(true);
  };

  const deleteFavoriteLocation = favoriteLocation => {
    dispatch(
      showConfirm(
        'Are you sure?',
        'Are you sure you want to remove this favorite location?',
        res => {
          if (res) {
            removeSavedLocation(favoriteLocation.GUID)
              .unwrap()
              .then(() => {
                toast.success(`Location removed successfully`, {
                  theme: 'colored',
                  position: 'bottom-center',
                  icon: faUserCheck,
                  toastId: 'login-success'
                });
              })
              .catch(() => {
                showAlert(
                  'Error Deleting Favorite location',
                  'There was a problem removing this favoritel Location.  Please try again.'
                );
              });
          }
        }
      )
    );
  };

  const addNewLocation = () => {
    setPreAddress(null);
    setFormTitle('Add new location');
    setShowAddressForm(true);
  };

  const onSearchKeywordChange = useAsyncDebounce(keyword => {
    setSearchKeyword(keyword);
    setPageIndex(0);
  }, 400);

  useEffect(() => {
    if (favoriteLocationLoadError) {
      logger.log('error loading favorite locations', enumerateError);
    }
  }, [favoriteLocationLoadError, enumerateError]);

  useEffect(() => {
    setPageIndex(0);
  }, [pageSize]);

  useEffect(() => {
    const list = enumerateRes?.Objects || [];
    setFavoriteLocations(list);
    setTotalRecords(enumerateRes?.TotalRecords ?? 0);
    setEndOfResults(!!enumerateRes?.EndOfResults);
  }, [enumerateRes]);

  useEffect(() => {
    refetch();
  }, []);

  const favoriteLocationsDisplay = favoriteLocations;

  return (
    <>
      {/* <PageHeader
        preTitle="Locations"
        title="Favorite Location list"
        description=""
        className="mb-3"
        titleTag="h2"
        image={corner3}
      /> */}

      <Row className="mb-3 g-3">
        <Col lg={12}>
          <FalconComponentCard noGuttersBottom>
            <FalconComponentCard.Header
              image={corner3}
              className={'px-4'}
              title="Favorite Locations"
              subtitle="Browse all favorite locations"
              endEl={
                <Flex className={'g-2'}>
                  <div>
                    <Form as={Row} className="gx-2 d-flex align-items-center">
                      <Col xs="auto">
                        <FormLabel className="mb-0">Sort by:</FormLabel>
                      </Col>
                      <Col xs="auto">
                        <InputGroup>
                          <Form.Select
                            size="sm"
                            className="pe-5"
                            defaultValue="Created"
                            onChange={({ target }) => {
                              setSortBy(target.value);
                              setPageIndex(0);
                            }}
                          >
                            {/* <option value="DisplayName">Name</option> */}
                            <option value="Created">Created</option>
                          </Form.Select>
                          <InputGroup.Text
                            as={Button}
                            variant="link"
                            className="border border-300 text-700"
                            onClick={() => {
                              const nextAsc = !isAsc;
                              setIsAsc(nextAsc);
                              setPageIndex(0);
                            }}
                          >
                            <FontAwesomeIcon
                              icon={
                                isAsc ? 'sort-amount-up' : 'sort-amount-down'
                              }
                            />
                          </InputGroup.Text>
                        </InputGroup>
                      </Col>
                    </Form>
                  </div>
                  <div className="mx-1">
                    <Form.Control
                      value={searchInput}
                      onChange={e => {
                        const next = e.target.value;
                        setSearchInput(next);
                        onSearchKeywordChange(next);
                      }}
                      placeholder="Search location..."
                      name="search"
                    ></Form.Control>
                  </div>
                  <div className="mx-1">
                    <Button variant="primary" onClick={() => addNewLocation()}>
                      <FontAwesomeIcon icon={faPlusCircle} size="sm" /> Add New
                    </Button>
                  </div>
                </Flex>
              }
            />
            <FalconComponentCard.Body noLight className="py-0 px-4">
              {isLoading && (
                <Flex justifyContent="center" className="py-3">
                  <Spinner size="sm" />
                </Flex>
              )}
              {favoriteLocations.length > 0 && !isLoading && (
                <Pagination
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  totalRecords={totalRecords}
                  currentCount={favoriteLocationsDisplay.length}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  endOfResults={endOfResults}
                  disabled={loading}
                  onRefresh={() => refetch()}
                  loading={loading}
                />
              )}
              {favoriteLocationsDisplay.length !== 0 &&
                !favoriteLocationLoadError &&
                !isLoading && (
                  <SimpleBarReact>
                    <Table
                      striped
                      bordered
                      className={loading ? 'refreshing-skeleton' : undefined}
                    >
                      <colgroup>
                        <col />
                        <col />
                        <col className="d-sm-none d-md-block" />
                        <col />
                      </colgroup>
                      <thead>
                        <tr>
                          <th scope="col">Name</th>
                          <th scope="col">Location</th>
                          <th scope="col">Created</th>
                          <th className="text-end" scope="col">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {_.map(favoriteLocationsDisplay, e => {
                          let createdDate = moment
                            .utc(e.Created)
                            .local()
                            .format('l, LT');

                          return (
                            <tr key={e.GUID}>
                              <td>{e.DisplayName}</td>
                              <td>
                                {e.Address1 ? e.Address1 + ', ' : ''}{' '}
                                {e.Address2 ? e.Address2 + ', ' : ''} {e.City}{' '}
                                {locationHelper.getStateNameByCode(e.State)} -{' '}
                                {e.PostalCode}
                              </td>
                              <td>{createdDate}</td>
                              <td className="text-end">
                                {/* <ActionButton
                                  icon="star"
                                  title={
                                    e.IsFavorite
                                      ? 'Remove Favorite'
                                      : 'Mark Favorite'
                                  }
                                  variant="action"
                                  className={
                                    e.IsFavorite
                                      ? 'p-0 me-3 text-primary'
                                      : 'p-0 me-3 text-secondary'
                                  }
                                  onClick={() => MarkFavoriteLocation(e)}
                                /> */}

                                <ActionButton
                                  icon="edit"
                                  title="Edit"
                                  label=""
                                  variant="action"
                                  className="p-0 me-3 text-primary"
                                  onClick={() => editFavoriteLocation(e)}
                                />
                                <ActionButton
                                  icon="trash-alt"
                                  title="Remove"
                                  label=""
                                  variant="action"
                                  className="p-0 text-danger"
                                  onClick={() => deleteFavoriteLocation(e)}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </SimpleBarReact>
                )}
              {favoriteLocations.length === 0 &&
                !loading &&
                (favoriteLocationLoadError ? (
                  <div className="text-center">
                    <FontAwesomeIcon icon={faLocationArrow} size="3x" />
                    <br />
                    <p className="my-3">
                      Unable to load favorite location list. Try again
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <FontAwesomeIcon icon={faLocationArrow} size="3x" />
                    <br />
                    <p className="my-3">
                      You don't have any favorite locations yet. <br />
                      Click "Add new" to add a location to your favorites. We
                      recommend adding your office or any regular place of
                      business where you normally meet with clients
                    </p>
                  </div>
                ))}
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Col>
      </Row>

      <Offcanvas
        id="offcanvas-address-form"
        show={showAddressForm}
        backdrop="static"
        onHide={() => setShowAddressForm(false)}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{formTitle}</Offcanvas.Title>
        </Offcanvas.Header>
        <hr className="my-0" />
        <Offcanvas.Body>
          <FavoriteAddressForm
            preAddress={preAddress}
            setAddressValue={saveFavouriteLocation}
            setShowAddressForm={setShowAddressForm}
            savingLocation={savingLocation}
            register={register}
            errors={{}}
            col={12}
          />
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default FavoriteLocations;
